import React, { useRef } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { Formik } from "formik";
import * as Yup from "yup";
import Container from "react-bootstrap/Container";
import emailjs from "@emailjs/browser";

function ContactForm({ showAddressFields = true }) { // Add the prop with default value true
  const formRef = useRef(); // Create a form reference using useRef

  const schema = Yup.object().shape({
    firstName: Yup.string().required("Vorname bitte angeben."),
    lastName: Yup.string().required("Nachname bitte angeben."),
    email: Yup.string()
      .email("Ungültige E-Mail-Adresse")
      .required("Bitte E-Mail Adresse eingeben."),
    telefonnumber: Yup.string().matches(
      /^\d+$/,
      "Telefonnummer muss aus Ziffern bestehen."
    ),
    terms: Yup.bool()
      .required()
      .oneOf([true], "AGB müssen akzeptiert werden."),
    dataprotection: Yup.bool()
      .required()
      .oneOf([true], "Datenschutzbestimmungen müssen akzeptiert werden."),
    
      // Only validate the address fields if they are shown
    city: showAddressFields ? Yup.string().required("Adresse bitte angeben.") : Yup.string(),
    state: showAddressFields ? Yup.string().required("Ort bitte angeben.") : Yup.string(),
    zip: showAddressFields ? Yup.string().required("PLZ bitte angeben.") : Yup.string(),
  });

  const sendEmail = (values, setSubmitting, resetForm) => {
    const templateParams = {
      firstName: values.firstName,
      lastName: values.lastName,
      city: values.city,
      state: values.state,
      zip: values.zip,
      email: values.email,
      telefonnumber: values.telefonnumber,
      remark: values.remark,
    };
  
    emailjs.send('service_rjmjpkq', 'template_j4svqro', templateParams, '_Vy4_yVElTn8uhx2H')
      .then((result) => {
        console.log(result.text);
        alert('Message sent successfully!');
        setSubmitting(false);
        resetForm(); // Optionally reset the form after successful submission
      }, (error) => {
        console.log(error.text);
        alert('Failed to send the message. Please try again.');
        setSubmitting(false);
      });
  };

  return (
    <Container>
      <Formik
        validationSchema={schema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          sendEmail(values, setSubmitting, resetForm); // Use sendForm to send email with form reference
        }}
        initialValues={{
          firstName: "",
          lastName: "",
          city: "",
          state: "",
          zip: "",
          email: "",
          telefonnumber: "",
          terms: false,
          dataprotection: false,
        }}
      >
        {(formik) => (
          <Form ref={formRef} noValidate onSubmit={formik.handleSubmit}>
            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="contactFormFirstName">
                <Form.Label>Vorname</Form.Label>
                <Form.Control className="mt-0"
                  type="text"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  isValid={formik.touched.firstName && !formik.errors.firstName}
                  isInvalid={
                    formik.touched.firstName && !!formik.errors.firstName
                  }
                />
                <Form.Control.Feedback type="valid">
                  Schaut gut aus!
                </Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="contactFormLastName">
                <Form.Label>Nachname</Form.Label>
                <Form.Control className="mt-0"
                  type="text"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  isValid={formik.touched.lastName && !formik.errors.lastName}
                  isInvalid={
                    formik.touched.lastName && !!formik.errors.lastName
                  }
                />
                <Form.Control.Feedback type="valid">
                  Schaut gut aus!
                </Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            {/* Conditionally render address fields */}
            {showAddressFields && (
              <Row className="mb-3">
                <Form.Group as={Col} md="6" controlId="contactFormCity">
                  <Form.Label>Adresse</Form.Label>
                  <Form.Control className="mt-0"
                    type="text"
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
                <Form.Group as={Col} md="3" controlId="contactFormState">
                  <Form.Label>Ort</Form.Label>
                  <Form.Control className="mt-0"
                    type="text"
                    name="state"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
                <Form.Group as={Col} md="3" controlId="contactFormZip">
                  <Form.Label>PLZ</Form.Label>
                  <Form.Control className="mt-0"
                    type="text"
                    name="zip"
                    value={formik.values.zip}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
              </Row>
            )}

            <Row className="mb-3">
              <Form.Group as={Col} md="6" controlId="contactFormEmail">
                <Form.Label>E-Mail</Form.Label>
                <Form.Control className="mt-0"
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  isValid={formik.touched.email && !formik.errors.email}
                  isInvalid={formik.touched.email && !!formik.errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.email}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="contactFormTelefonnumber">
                <Form.Label>Telefonnummer</Form.Label>
                <Form.Control className="mt-0"
                  type="tel"
                  name="telefonnumber"
                  value={formik.values.telefonnumber}
                  onChange={formik.handleChange}
                  isInvalid={!!formik.errors.telefonnumber}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.telefonnumber}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md="12" controlId="contactFormRemarks">
                <Form.Label>Anmerkungen</Form.Label>
                <Form.Control className="custom-textarea"
                  as="textarea"
                  rows={3}
                  name="remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                  isValid={formik.touched.remark && !formik.errors.remark}
                />
              </Form.Group>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                required
                name="terms"
                label="Ich habe die AGB gelesen und stimme zu."
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.terms}
                feedback={formik.errors.terms}
                feedbackType="invalid"
                id="validationFormik0"
              />
              <Form.Check
                required
                name="dataprotection"
                label="Ich nehme die Datenschutzerklärung zur Kenntnis."
                onChange={formik.handleChange}
                isInvalid={!!formik.errors.dataprotection}
                feedback={formik.errors.dataprotection}
                feedbackType="invalid"
                id="validationFormik0"
              />
            </Form.Group>

            <Button type="submit" disabled={formik.isSubmitting}>
              Anfrage senden
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

export default ContactForm;
